import { useFormField } from "@faire/retailer-visitor-shared/components/SignUpForm/FormField";
import {
  isFaireEmail,
  isInvalidEmail,
} from "@faire/retailer-visitor-shared/components/SignUpForm/validate";
import { isValidEmail } from "@faire/retailer-visitor-shared/lib/validation";
import { useSettingFaireDirectSignupEmailAutofill } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectSignupEmailAutofill";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import checkUserAvailability from "@faire/web-api--source/endpoints/www/api/user/availability/post";
import { ICheckEmailAddressAndBusinessAvailabilityRequest } from "@faire/web-api--source/indigofair/data/ICheckEmailAddressAndBusinessAvailabilityRequest";
import { QueryParameters as SignUpQueryParameters } from "@faire/web-api--source/routes/www/welcome/r/personal";
import { useCallback } from "react";

const EVENT_NAME = "email";
const EVENT_PREFIX = "sign-up-page/modal";

export const useEmailState = (isFaireDirectSignup: boolean) => {
  const signUpParams = useQueryParams(SignUpQueryParameters.parse);
  const settingFaireDirectSignupEmailAutofill =
    useSettingFaireDirectSignupEmailAutofill();
  const { strictLocalize } = useStrictLocalization();

  const emailFromSignupParams =
    !!signUpParams.email && isValidEmail(signUpParams.email)
      ? signUpParams.email
      : "";
  const initialValue =
    !isFaireDirectSignup || settingFaireDirectSignupEmailAutofill
      ? emailFromSignupParams
      : "";

  const email = useFormField(
    initialValue,
    EVENT_NAME,
    isInvalidEmail,
    EVENT_PREFIX
  );

  const setEmailValidationErrors = useCallback(() => {
    if (email.isInvalid) {
      const emailError = strictLocalize({
        defaultMessage: "Please enter a valid email address",
        description: {
          text: "Error message shown to user when invalid email address is entered",
        },
      });
      email.setError(emailError);
      return true;
    }
    return false;
  }, [email, strictLocalize]);

  const setSomethingWentWrongEmailError = useCallback(
    (error: unknown) => {
      const defaultMessage = strictLocalize({
        defaultMessage: "Something went wrong. Please try again later.",
        description: {
          text: "This error states that an unexpected error occurred",
        },
      });
      email.setError(
        getWebApiErrorMessage(error, strictLocalize, defaultMessage)
      );
    },
    [email, strictLocalize]
  );

  const getIsEmailAvailable = useCallback(async () => {
    const trimmedEmailAddress = email.value.trim();
    const { email_address_available } = await checkUserAvailability(
      ICheckEmailAddressAndBusinessAvailabilityRequest.build({
        email_address: trimmedEmailAddress,
      })
    );
    if (email_address_available) {
      if (isFaireEmail(email.value)) {
        const emailError = strictLocalize({
          defaultMessage:
            "Use your Faire email address with a + segment (e.g. your.email+test@faire.com)",
          description: {
            text: "Error message shown to user when trying to create an account with a faire email.",
            img: "https://cdn.faire.com/fastly/6318e502de81a22dc4623061cc0d6539d5af1817a27d8a636c60ed89add393b0.png",
          },
        });
        email.setError(emailError);
        return false;
      }
      return true;
    }
    return false;
  }, [email, strictLocalize]);

  return {
    email,
    getIsEmailAvailable,
    setEmailValidationErrors,
    setSomethingWentWrongEmailError,
  };
};
